import classNames from 'classnames'
import React, { useImperativeHandle } from 'react'
import { Col, Input, InputProps, InputRef, Row } from 'src/antd'
import { useConditionalPersistentState } from 'src/hooks/useConditionalPersistentState'
import { THookDebouncedArgs, useDebouncedInput } from './create-debounced-input'

type TExtendInputProps = Omit<InputProps, 'value' | 'onChange' | 'disabled'>

type TInputPropsExtended<K> = {
  key: K
  getRef?: (r: InputRef) => void
}

export type TTDebouncedSearchExpandableGroupRef<V> = {
  setValue: (v: V) => void
  getValue?: () => V
}

export type TDebouncedSearchExpandableGroupProps<V = RSANullable> = {
  persistentKey?: string
  inputs: Array<
    TInputPropsExtended<keyof Exclude<V, undefined>> & TExtendInputProps
  >
  inputPropsAll?: TExtendInputProps
  getRef?: (R: TTDebouncedSearchExpandableGroupRef<V>) => void
  defaultValue?: V
} & THookDebouncedArgs<V>

export const SearchExpandableGroup = <V extends RSANullable>({
  inputs,
  inputPropsAll,
  getRef,
  withDebouncedConfig,
  persistentKey,
  defaultValue,
}: TDebouncedSearchExpandableGroupProps<V>) => {
  const [value, setValue] = useConditionalPersistentState<V>(
    defaultValue,
    persistentKey
  )

  useDebouncedInput({ withDebouncedConfig, value })

  useImperativeHandle(getRef, () => ({
    setValue: (v) => {
      if (v) {
        setValue(v)
      }
    },
    getValue: () => value,
  }))

  const colRatio = Math.floor(24 / inputs.length)

  return (
    <Row wrap={false} gutter={8}>
      {inputs.map(({ key, getRef: getInputRef, ...extended }) => (
        <Col key={key as string} xs={colRatio}>
          <Input
            {...inputPropsAll}
            {...extended}
            className={classNames(inputPropsAll?.className, extended.className)}
            style={{ ...inputPropsAll?.style, ...extended.style }}
            onChange={(e) => {
              setValue?.({ ...(value as V), [key]: e.target.value })
            }}
            // @ts-expect-error Wip
            value={value?.[key]}
            ref={getInputRef}
          />
        </Col>
      ))}
    </Row>
  )
}
